import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { F965BService } from '../../services/f965b.service';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-f965b',
    templateUrl: './f965b.component.html',
    styleUrls: ['./f965b.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F965BComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  activeTab = 1;
  
  // Input properties from AngularJS
  @Input('base-urls') baseURLs: any;
  @Input('capture-custom-events') captureCustomEvents: any;
  
  @Input('header-data')
  set headerData(headerData: any) {
    try {
      this.globalService = safeJsonInput<any>(headerData, null, 'f965b.parse1');
      this.GENERAL_CONFIG = this.globalService.generalConfigData;
      this.USER_SETTINGS = this.globalService.userSettingsData;
      this.init();
    } catch (e) {
      console.log("Header data input not yet stable. Still waiting to get resolved.");
    }
  }
  
  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      this.rowData = safeJsonInput<any>(rowData, null, 'f965b.parse2');
      this.init();
    } catch (e) {
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }
  
  @Input('filter-params') set _filter_params(filterParams: string) {
    try {
      let params = safeJsonInput<any>(filterParams, null, 'f965b.parse3');
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        this.init();
      }
    } catch (e) {
      console.log("Filter params input not yet stable. Still waiting to get resolved.");
    }
  }

  // Component properties
  modalTitle = "Corporate and Real Estate Investment Trust (REIT) Report of Net 965 Tax Liability and Electing REIT Report of 965 Amounts";
  loading = false;
  showFormLoader = false;
  crudLoading = false;
  dataLoaded = false; // Flag to prevent multiple API calls
  
  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';
  
  // Form data properties
  scheduleBFormView: any = { rows: [] };
  formListOriginal: any = {};
  partI: any = { header: [], rows: [] };
  partII: any = { header: [], rows: [] };
  partIII: any = { header: [], rows: [] };
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  mainData: any[] = [];
  
  // Services and configurations
  globalService: any;
  filterParams: any = {};
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  rowData: any = {};
  
  // Dynamic attributes array
  dynamic_attr = ['NA', 'NA', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l'];

  constructor(
    private f965bService: F965BService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    // Initial setup will be done via inputs
  }

  init(): void {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.dataLoaded) {
        this.setupEntityData();
        this.getModalData();
        this.dataLoaded = true; // Mark data as loaded to prevent multiple calls
      }
    } catch (e) {
      console.error('Error in F965B init:', e);
    }
  }

  private setupEntityData(): void {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];
  }

  getModalData(): void {
    this.loading = true;
    this.showFormLoader = true;

    const params = {
      "action_id": 31742,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY,
      "schedule_name": "B"
    };

    this.f965bService.loadF965BData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      data => {
        if (data.callSuccess && data.callSuccess.length > 0 && data.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(`Error loading data: ${data.errorMessage}`, 'gtw-snackbar--red', true, 4);
        } else {
          if (data.irsForm && data.irsForm.formItemList && data.irsForm.formItemList.length > 0) {
            this.processF965BData(data.irsForm.formItemList);
          } else {
            this.gtwSnackbar.openSnackBar('No data found!', 'gtw-snackbar--yellow', true, 4);
          }
        }
        this.loading = false;
        this.showFormLoader = false;
      },
      error => {
        console.error('Error fetching F965B data:', error);
        this.gtwSnackbar.openSnackBar('Failed to load data.', 'gtw-snackbar--red', true, 4);
        this.loading = false;
        this.showFormLoader = false;
      }
    );
  }

  processF965BData(formItemList: any[]): void {
    // Get title from first item
    if (formItemList[0] && formItemList[0].dataRowList && formItemList[0].dataRowList[0]) {
      this.modalTitle = formItemList[0].dataRowList[0].dataCellList[1].name;
    }

    // Process form data starting from index 1
    for (let i = 1; i < formItemList.length; i++) {
      const saveParamDtls = formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = formItemList[i].dataRowList[0].dataCellList.length;
      
      const tempDataObj: any = {};
      tempDataObj.num = formItemList[i].dataRowList[0].dataCellList[0].name;
      tempDataObj.description = formItemList[i].dataRowList[0].dataCellList[1].name;
      
      if (rows > 2) {
        this.iterateArray(rows, formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      this.scheduleBFormView.rows.push(tempDataObj);
    }

    // Store original copy
    this.formListOriginal = _.cloneDeep(this.scheduleBFormView);

    // Set initial values
    this.scheduleBFormView.rows[1].value = this.rowData.LE_NAME;
    this.scheduleBFormView.rows[2].value = this.rowData.EIN;
    this.scheduleBFormView.rows[3].value = this.rowData.TAX_YEAR;

    // Setup parts
    this.partI.header = this.scheduleBFormView.rows.slice(5, 7);
    this.partI.rows = this.scheduleBFormView.rows.slice(7, 15);
    this.partII.header = this.scheduleBFormView.rows.slice(15, 17);
    this.partII.rows = this.scheduleBFormView.rows.slice(17, 26);
    this.partIII.header = this.scheduleBFormView.rows.slice(26, 28);
    this.partIII.rows = this.scheduleBFormView.rows.slice(28, 37);

    this.onLoadComputeTotals();
  }

  iterateArray(count: number, array: any[], tempDataObj: any): void {
    if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.value = array[2].value === 'true' ? true : array[2].value === 'false' ? false : array[2].value;
    } else {
      tempDataObj.list = {};
      tempDataObj.attributeList = [];
      for (let i = 2; i < array.length; i++) {
        tempDataObj["lineAttrKey_" + this.dynamic_attr[i]] = array[i].key.split('~')[1];
        tempDataObj["attrib_name_" + this.dynamic_attr[i]] = array[i].key.split('~')[0];
        tempDataObj["attrib_order_" + this.dynamic_attr[i]] = array[i].key.split('~')[2];
        tempDataObj["attrib_value_" + this.dynamic_attr[i]] = array[i].value;
        tempDataObj.list["attrib_value_" + this.dynamic_attr[i]] = array[i].value === 'true' ? true : array[i].value === 'false' ? false : array[i].value;
        if (i === 2) {
          tempDataObj.isEditable = tempDataObj.attrib_value_a == null ? 'Y' : 'N';
        }
        tempDataObj.attributeList.push("attrib_value_" + this.dynamic_attr[i]);
      }
    }
  }

  computeRow(part: string, i: number): void {
    const partData = this[part as keyof F965BComponent] as any;
    partData.rows[i].list.attrib_value_d = partData.rows[i].list.attrib_value_c - partData.rows[i].list.attrib_value_b;
    if (partData.rows[i].list.attrib_value_e) {
      partData.rows[i].list.attrib_value_f = 0;
      partData.rows[i].list.attrib_value_g = partData.rows[i].list.attrib_value_d;
    } else {
      partData.rows[i].list.attrib_value_g = 0;
      partData.rows[i].list.attrib_value_f = partData.rows[i].list.attrib_value_d;
    }
  }

  computeCalculationRows(part: string, rows: number, index: number, keyName: string): void {
    if (keyName === 'attrib_value_b' || keyName === 'attrib_value_c' || keyName === 'all') {
      if (rows > 1) {
        for (let i = 0; i < rows; i++) {
          this.computeRow(part, i);
        }
      } else {
        this.computeRow(part, index);
      }
    }
  }

  // Helper method to get keys from row.list object (replicates AngularJS ng-repeat behavior)
  getObjectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  computeTotalValues(part: string, attr: string, totalIndex: number, totalAttr: string): void {
    let total = 0;
    const partData = this[part as keyof F965BComponent] as any;
    for (let i = 0; i < partData.rows.length; i++) {
      if (!!partData.rows[i].list[attr]) {
        const value = parseInt(partData.rows[i].list[attr]);
        total += value;
      }
    }
    partData.rows[totalIndex].list[totalAttr] = total;
  }

  onLoadComputeTotals(): void {
    this.computeCalculationRows('partI', 8, 0, 'all');
    this.computeTotalValues('partII', 'attrib_value_j', 8, 'attrib_value_a');
    this.computeTotalValues('partII', 'attrib_value_k', 8, 'attrib_value_b');
    this.computeTotalValues('partIII', 'attrib_value_k', 8, 'attrib_value_a');
    this.computeTotalValues('partIII', 'attrib_value_l', 8, 'attrib_value_b');
  }

  checkBoxToggle(index: number, keyName: string, type: string, event: any): void {
    const isChecked = event.target.checked;
    
    if (type === 'yes') {
      if (isChecked) {
        // Yes checkbox checked - set value to true
        this.partI.rows[index].list[keyName] = true;
        this.partI.rows[index].list.attrib_value_f = 0;
        this.partI.rows[index].list.attrib_value_g = this.partI.rows[index].list.attrib_value_d;
      } else {
        // Yes checkbox unchecked - set to NA
        this.partI.rows[index].list[keyName] = 'NA';
        this.partI.rows[index].list.attrib_value_f = 0;
        this.partI.rows[index].list.attrib_value_g = 0;
      }
    } else if (type === 'no') {
      if (isChecked) {
        // No checkbox checked - set value to false
        this.partI.rows[index].list[keyName] = false;
        this.partI.rows[index].list.attrib_value_g = 0;
        this.partI.rows[index].list.attrib_value_f = this.partI.rows[index].list.attrib_value_d;
      } else {
        // No checkbox unchecked - set to NA
        this.partI.rows[index].list[keyName] = 'NA';
        this.partI.rows[index].list.attrib_value_f = 0;
        this.partI.rows[index].list.attrib_value_g = 0;
      }
    }
  }

  save(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.showFormLoader = true;

    // Check for unsaved changes
    if (_.isEqual(this.scheduleBFormView, this.formListOriginal)) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.showFormLoader = false;
      this.gtwSnackbar.openSnackBar("No changes to save.", 'gtw-snackbar--yellow', true, 4);
      return;
    }

    // Build save data
    const tempArray: any[] = [];
    
    for (let i = 0; i < this.scheduleBFormView.rows.length; i++) {
      if (this.scheduleBFormView.rows[i].num !== 'NA') {
        if (this.scheduleBFormView.rows[i].list) {
          for (let j = 0; j < this.scheduleBFormView.rows[i].attributeList.length; j++) {
            if (this.scheduleBFormView.rows[i].list["attrib_value_" + this.dynamic_attr[j + 2]] !== null) {
              const formRowDataA: any = {
                tax_year: this.rowData.TAX_YEAR,
                trans_type_key: this.scheduleBFormView.rows[i].saveParams[1],
                line_order: this.scheduleBFormView.rows[i].line_order,
                attrib_order: this.scheduleBFormView.rows[i]["attrib_order_" + this.dynamic_attr[j + 2]],
                line_description: this.scheduleBFormView.rows[i].description,
                combination_key: this.rowData.COMBINATION_KEY,
                group_obj_key: this.rowData.GROUP_OBJ_KEY,
                form_key: this.scheduleBFormView.rows[i].saveParams[2],
                line_no: this.scheduleBFormView.rows[i].num,
                occurence: this.scheduleBFormView.rows[i].saveParams[7],
                line_attrib_key: this.scheduleBFormView.rows[i]["lineAttrKey_" + this.dynamic_attr[j + 2]],
                Attribute_Name: this.scheduleBFormView.rows[i]["attrib_name_" + this.dynamic_attr[j + 2]],
                Attribute_Value: this.scheduleBFormView.rows[i].list["attrib_value_" + this.dynamic_attr[j + 2]],
                trans_dtls_key: this.scheduleBFormView.rows[i].saveParams[3],
                trans_status: this.scheduleBFormView.rows[i].saveParams[6],
                is_dirty: ''
              };
              tempArray.push(formRowDataA);
            }
          }
        } else {
          const formRowDataB: any = {
            tax_year: this.rowData.TAX_YEAR,
            trans_type_key: this.scheduleBFormView.rows[i].saveParams[1],
            line_order: this.scheduleBFormView.rows[i].line_order,
            attrib_order: this.scheduleBFormView.rows[i].attrib_order_a,
            line_description: this.scheduleBFormView.rows[i].description,
            combination_key: this.rowData.COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: this.scheduleBFormView.rows[i].saveParams[2],
            line_no: this.scheduleBFormView.rows[i].num,
            occurence: this.scheduleBFormView.rows[i].saveParams[7],
            line_attrib_key: this.scheduleBFormView.rows[i].lineAttrKey_a,
            Attribute_Name: this.scheduleBFormView.rows[i].attrib_name_a,
            Attribute_Value: this.scheduleBFormView.rows[i].value,
            trans_dtls_key: this.scheduleBFormView.rows[i].saveParams[3],
            trans_status: this.scheduleBFormView.rows[i].saveParams[6],
            is_dirty: ''
          };
          tempArray.push(formRowDataB);
        }
      }
    }

    this.formData.editrows = [tempArray];
    this.mainData = [this.formData];

    // Call service to save
    this.f965bService.saveF965BForm(
      this.GENERAL_CONFIG.base_url,
      this.mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      response => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.showFormLoader = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          // ✅ SUCCESS: Emit STRING 'refresh' (NOT object)
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbar.openSnackBar(`Error saving data: ${response.errorMessage}`, 'gtw-snackbar--red', true, 4);
        }
      },
      error => {
        console.error('Error saving F965B data:', error);
        this.gtwSnackbar.openSnackBar('Failed to save data.', 'gtw-snackbar--red', true, 4);
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.showFormLoader = false;
      }
    );
  }

  reset(): void {
    this.partI.header = _.cloneDeep(this.formListOriginal.rows.slice(5, 7));
    this.partI.rows = _.cloneDeep(this.formListOriginal.rows.slice(7, 15));
    this.partII.header = _.cloneDeep(this.formListOriginal.rows.slice(15, 17));
    this.partII.rows = _.cloneDeep(this.formListOriginal.rows.slice(17, 26));
    this.partIII.header = _.cloneDeep(this.formListOriginal.rows.slice(26, 28));
    this.partIII.rows = _.cloneDeep(this.formListOriginal.rows.slice(28, 37));
    this.onLoadComputeTotals();
    
    this.gtwSnackbar.openSnackBar('Form reset.', 'gtw-snackbar--yellow', true, 2);
  }

  cancel(): void {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
  }
}
